"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useProjectText } from "@/features/project/useProjectText";
import { cn } from "@/lib/cn";
import { useItemsOrder } from "../hooks/useItemsOrder";
import { useResetPage } from "../hooks/usePage";
import { useSelectedItemId } from "../hooks/useSelectedItemId";
import type { OrderOption } from "@/types";

/**
 * The result list's sort control.
 *
 * Shown only when the tenant enabled it and authored options — the label of each
 * comes from their own strings under `orderBy.<field>`, since a sort by
 * "annee-livraison" needs to read as "Delivery year".
 */
export function ListOrderBy() {
  const t = useTranslations();
  const projectText = useProjectText();
  const { order, setOrder, listOrderOptions, showListOrder } = useItemsOrder();
  const { selectedItemId } = useSelectedItemId();
  const resetPage = useResetPage();
  const [isOpen, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen]);

  if (!showListOrder || !listOrderOptions?.length) return null;

  const label = (option: OrderOption | null) =>
    projectText(`orderBy.${option?.field}`, option?.field ?? "");

  const choose = (option: OrderOption) => {
    setOrder(option);
    // A different order makes "page 3" a different set of results.
    resetPage();
    setOpen(false);
  };

  return (
    <div className="sort grid auto-cols-max grid-flow-col items-center justify-self-end text-right text-xs">
      <div ref={ref} className="relative inline-block">
        <button
          type="button"
          // The details panel covers the list, so its controls must not be
          // reachable by tab from behind it.
          tabIndex={selectedItemId ? -1 : 0}
          onClick={() => setOpen(!isOpen)}
          aria-expanded={isOpen}
          className="flex transform items-center transition-transform duration-300 focus-visible:outline focus-visible:outline-blue-500"
        >
          {t("sortBy")} {label(order)}
          <i
            className={cn(
              "icon ml-2 w-4 text-2xl !leading-[0.8]",
              isOpen
                ? "ic-material_ic_keyboard_arrow_up_48px"
                : "ic-material_ic_keyboard_arrow_down_48px"
            )}
            aria-hidden
          />
        </button>

        <div
          className={cn(
            "overflow-hidden transition-all duration-300 ease-in-out",
            isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div
            className="absolute mt-2 w-full rounded-xl border border-gray-300 bg-white dark:bg-dark-color"
            style={{ boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.25)" }}
          >
            {listOrderOptions.map((option, index) => (
              <button
                key={option.field ?? index}
                type="button"
                tabIndex={isOpen ? 0 : -1}
                onClick={() => choose(option)}
                className={cn(
                  "dropdown-item w-full px-4 py-2 text-left text-sm",
                  "hover:bg-primary hover:dynamic-text dark:hover:bg-primary",
                  order?.field === option.field
                    ? "bg-primary dynamic-text"
                    : "bg-white dark:bg-black",
                  index === 0 && "rounded-t-lg",
                  index === listOrderOptions.length - 1 && "rounded-b-lg"
                )}
              >
                {label(option)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
